import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AppsPageLayout } from 'views/layouts/AppsPageLayout';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';
import { AppState, useAppDispatch } from 'stores/type';
import { DefaultTemplate } from 'views/templates/DefaultTemplate';
import { Splash } from 'views/components/molecules/Splash';
import { DeviceDomain } from 'models/Device';
import { AvailableApps } from 'models/Device/type';
import { closeDialog, showDialog } from 'stores/dialogSlice';
import { localeIds } from 'locals';
import { useIntl } from 'react-intl';
import { setExceptionError } from 'stores/errorSlice';
import { ROUTER_PATH } from 'views/constants';
import { saveEmail } from 'stores/deviceSlice';
import { NotifyDomain } from 'models/Notify';
import { useDevice } from 'views/hooks/useDevice';

export const AppsPage: React.FC = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const useSelector: TypedUseSelectorHook<AppState> = useReduxSelector;
  const device = useSelector((state) => state.deviceState.device);
  const { logout, reInstallConfigurationProfile } = useDevice();
  const deviceDomain = new DeviceDomain(device);
  const notifyDomain = NotifyDomain.getInstance();
  const [availableApps, setAvailableApps] = useState<AvailableApps | undefined>(
    undefined
  );

  const handleCloseDialog = useCallback(() => {
    dispatch(closeDialog());
  }, [dispatch]);

  const fetchDeviceEmail = useCallback(async () => {
    if (!device) {
      return;
    }

    DeviceDomain.getEmail()
      .then((result) => {
        if (result.isSuccess() && result.value) {
          dispatch(saveEmail(result.value));
        }
      })
      .catch((e) => {
        // 通常はエラーが返ってくることはないが一応ログに出しておく
        console.log(e);
        return;
      });
  }, []);

  const subscribeDistributionLeave = useCallback((): void => {
    notifyDomain.subscribeDistributionLeave(() => {
      history.replace(ROUTER_PATH.home);
    }, deviceDomain.value?.uid);
  }, []);

  const fetchAvailableApps = useCallback(async (): Promise<void> => {
    deviceDomain
      .availableApps()
      .then((result) => {
        if (result.isFailure()) {
          dispatch(
            showDialog({
              content: intl.formatMessage({ id: result.value.localeId() }),
              defaultAction: {
                label: intl.formatMessage({ id: localeIds.dialog.ok }),
                callback: () => {
                  dispatch(closeDialog());
                  history.replace(ROUTER_PATH.configurationProfile);
                },
              },
            })
          );
          return;
        }

        setAvailableApps(result.value);
      })
      .catch((e) => {
        dispatch(setExceptionError(e));
      });
  }, []);

  useEffect(() => {
    fetchAvailableApps();
    fetchDeviceEmail();
    subscribeDistributionLeave();
  }, []);

  const handleLogout = useCallback(() => {
    dispatch(
      showDialog({
        content: intl.formatMessage({
          id: localeIds.appsPage.confirm.logout,
        }),
        defaultAction: {
          label: intl.formatMessage({ id: localeIds.dialog.ok }),
          callback: async () => {
            dispatch(closeDialog());
            await logout(deviceDomain)
              .then(async () => {
                setAvailableApps(undefined);
                await fetchAvailableApps();
              })
              .catch((e) => {
                dispatch(setExceptionError(e));
              });
          },
        },
        cancelAction: {
          label: intl.formatMessage({ id: localeIds.dialog.cancel }),
          callback: handleCloseDialog,
        },
      })
    );
  }, [logout, deviceDomain]);

  const handleLogin = useCallback(() => {
    history.push(ROUTER_PATH.login);
  }, []);

  const handleReInstall = useCallback(() => {
    dispatch(
      showDialog({
        content: intl.formatMessage({
          id: localeIds.appsPage.confirm.reInstall,
        }),
        defaultAction: {
          label: intl.formatMessage({ id: localeIds.dialog.ok }),
          callback: async () => {
            dispatch(closeDialog());
            await reInstallConfigurationProfile().catch((e) => {
              dispatch(setExceptionError(e));
            });
          },
        },
        cancelAction: {
          label: intl.formatMessage({ id: localeIds.dialog.cancel }),
          callback: handleCloseDialog,
        },
      })
    );
  }, [reInstallConfigurationProfile]);

  const userInfo = device?.user
    ? {
        profileSrc: device?.user.profileIcon,
        name: device?.user.name,
        device: device?.detail?.deviceName,
        reInstallProfile: device?.detail?.updateProfileWarningAppears,
      }
    : { email: device?.email };

  return availableApps ? (
    <DefaultTemplate
      headerText={intl.formatMessage({ id: localeIds.appsPageLayout.title })}
      footerLinks={[
        {
          text: intl.formatMessage({
            id: localeIds.appsPage.terms.title,
          }),
          href: 'https://deploygate.com/terms',
        },
      ]}
    >
      <AppsPageLayout
        userInfo={userInfo}
        availableApps={availableApps}
        isLogin={deviceDomain.isAuthenticated()}
        onLogout={handleLogout}
        onLogin={handleLogin}
        onReInstall={handleReInstall}
      />
    </DefaultTemplate>
  ) : (
    <DefaultTemplate headerText="">
      <Splash />
    </DefaultTemplate>
  );
};
